import { property as V } from "lit/decorators.js";
import { html as vt } from "lit/static-html.js";
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const R = globalThis, K = R.ShadowRoot && (R.ShadyCSS === void 0 || R.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, W = Symbol(), Z = /* @__PURE__ */ new WeakMap();
let dt = class {
  constructor(o, t, e) {
    if (this._$cssResult$ = !0, e !== W) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = o, this.t = t;
  }
  get styleSheet() {
    let o = this.o;
    const t = this.t;
    if (K && o === void 0) {
      const e = t !== void 0 && t.length === 1;
      e && (o = Z.get(t)), o === void 0 && ((this.o = o = new CSSStyleSheet()).replaceSync(this.cssText), e && Z.set(t, o));
    }
    return o;
  }
  toString() {
    return this.cssText;
  }
};
const bt = (o) => new dt(typeof o == "string" ? o : o + "", void 0, W), $t = (o, ...t) => {
  const e = o.length === 1 ? o[0] : t.reduce((s, i, r) => s + ((n) => {
    if (n._$cssResult$ === !0) return n.cssText;
    if (typeof n == "number") return n;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + n + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(i) + o[r + 1], o[0]);
  return new dt(e, o, W);
}, gt = (o, t) => {
  if (K) o.adoptedStyleSheets = t.map((e) => e instanceof CSSStyleSheet ? e : e.styleSheet);
  else for (const e of t) {
    const s = document.createElement("style"), i = R.litNonce;
    i !== void 0 && s.setAttribute("nonce", i), s.textContent = e.cssText, o.appendChild(s);
  }
}, G = K ? (o) => o : (o) => o instanceof CSSStyleSheet ? ((t) => {
  let e = "";
  for (const s of t.cssRules) e += s.cssText;
  return bt(e);
})(o) : o;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: _t, defineProperty: yt, getOwnPropertyDescriptor: At, getOwnPropertyNames: Et, getOwnPropertySymbols: wt, getPrototypeOf: xt } = Object, A = globalThis, Y = A.trustedTypes, St = Y ? Y.emptyScript : "", Q = A.reactiveElementPolyfillSupport, P = (o, t) => o, M = { toAttribute(o, t) {
  switch (t) {
    case Boolean:
      o = o ? St : null;
      break;
    case Object:
    case Array:
      o = o == null ? o : JSON.stringify(o);
  }
  return o;
}, fromAttribute(o, t) {
  let e = o;
  switch (t) {
    case Boolean:
      e = o !== null;
      break;
    case Number:
      e = o === null ? null : Number(o);
      break;
    case Object:
    case Array:
      try {
        e = JSON.parse(o);
      } catch {
        e = null;
      }
  }
  return e;
} }, q = (o, t) => !_t(o, t), X = { attribute: !0, type: String, converter: M, reflect: !1, hasChanged: q };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), A.litPropertyMetadata ?? (A.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class y extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ?? (this.l = [])).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, e = X) {
    if (e.state && (e.attribute = !1), this._$Ei(), this.elementProperties.set(t, e), !e.noAccessor) {
      const s = Symbol(), i = this.getPropertyDescriptor(t, s, e);
      i !== void 0 && yt(this.prototype, t, i);
    }
  }
  static getPropertyDescriptor(t, e, s) {
    const { get: i, set: r } = At(this.prototype, t) ?? { get() {
      return this[e];
    }, set(n) {
      this[e] = n;
    } };
    return { get() {
      return i == null ? void 0 : i.call(this);
    }, set(n) {
      const l = i == null ? void 0 : i.call(this);
      r.call(this, n), this.requestUpdate(t, l, s);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? X;
  }
  static _$Ei() {
    if (this.hasOwnProperty(P("elementProperties"))) return;
    const t = xt(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(P("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(P("properties"))) {
      const e = this.properties, s = [...Et(e), ...wt(e)];
      for (const i of s) this.createProperty(i, e[i]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const e = litPropertyMetadata.get(t);
      if (e !== void 0) for (const [s, i] of e) this.elementProperties.set(s, i);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [e, s] of this.elementProperties) {
      const i = this._$Eu(e, s);
      i !== void 0 && this._$Eh.set(i, e);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const e = [];
    if (Array.isArray(t)) {
      const s = new Set(t.flat(1 / 0).reverse());
      for (const i of s) e.unshift(G(i));
    } else t !== void 0 && e.push(G(t));
    return e;
  }
  static _$Eu(t, e) {
    const s = e.attribute;
    return s === !1 ? void 0 : typeof s == "string" ? s : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var t;
    this._$ES = new Promise((e) => this.enableUpdating = e), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (t = this.constructor.l) == null || t.forEach((e) => e(this));
  }
  addController(t) {
    var e;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(t), this.renderRoot !== void 0 && this.isConnected && ((e = t.hostConnected) == null || e.call(t));
  }
  removeController(t) {
    var e;
    (e = this._$EO) == null || e.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), e = this.constructor.elementProperties;
    for (const s of e.keys()) this.hasOwnProperty(s) && (t.set(s, this[s]), delete this[s]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return gt(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    var t;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (t = this._$EO) == null || t.forEach((e) => {
      var s;
      return (s = e.hostConnected) == null ? void 0 : s.call(e);
    });
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    var t;
    (t = this._$EO) == null || t.forEach((e) => {
      var s;
      return (s = e.hostDisconnected) == null ? void 0 : s.call(e);
    });
  }
  attributeChangedCallback(t, e, s) {
    this._$AK(t, s);
  }
  _$EC(t, e) {
    var s;
    const i = this.constructor.elementProperties.get(t), r = this.constructor._$Eu(t, i);
    if (r !== void 0 && i.reflect === !0) {
      const n = (((s = i.converter) == null ? void 0 : s.toAttribute) !== void 0 ? i.converter : M).toAttribute(e, i.type);
      this._$Em = t, n == null ? this.removeAttribute(r) : this.setAttribute(r, n), this._$Em = null;
    }
  }
  _$AK(t, e) {
    var s;
    const i = this.constructor, r = i._$Eh.get(t);
    if (r !== void 0 && this._$Em !== r) {
      const n = i.getPropertyOptions(r), l = typeof n.converter == "function" ? { fromAttribute: n.converter } : ((s = n.converter) == null ? void 0 : s.fromAttribute) !== void 0 ? n.converter : M;
      this._$Em = r, this[r] = l.fromAttribute(e, n.type), this._$Em = null;
    }
  }
  requestUpdate(t, e, s) {
    if (t !== void 0) {
      if (s ?? (s = this.constructor.getPropertyOptions(t)), !(s.hasChanged ?? q)(this[t], e)) return;
      this.P(t, e, s);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(t, e, s) {
    this._$AL.has(t) || this._$AL.set(t, e), s.reflect === !0 && this._$Em !== t && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(t);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (e) {
      Promise.reject(e);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var t;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [r, n] of this._$Ep) this[r] = n;
        this._$Ep = void 0;
      }
      const i = this.constructor.elementProperties;
      if (i.size > 0) for (const [r, n] of i) n.wrapped !== !0 || this._$AL.has(r) || this[r] === void 0 || this.P(r, this[r], n);
    }
    let e = !1;
    const s = this._$AL;
    try {
      e = this.shouldUpdate(s), e ? (this.willUpdate(s), (t = this._$EO) == null || t.forEach((i) => {
        var r;
        return (r = i.hostUpdate) == null ? void 0 : r.call(i);
      }), this.update(s)) : this._$EU();
    } catch (i) {
      throw e = !1, this._$EU(), i;
    }
    e && this._$AE(s);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    var e;
    (e = this._$EO) == null || e.forEach((s) => {
      var i;
      return (i = s.hostUpdated) == null ? void 0 : i.call(s);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((e) => this._$EC(e, this[e]))), this._$EU();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
}
y.elementStyles = [], y.shadowRootOptions = { mode: "open" }, y[P("elementProperties")] = /* @__PURE__ */ new Map(), y[P("finalized")] = /* @__PURE__ */ new Map(), Q == null || Q({ ReactiveElement: y }), (A.reactiveElementVersions ?? (A.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const j = globalThis, L = j.trustedTypes, tt = L ? L.createPolicy("lit-html", { createHTML: (o) => o }) : void 0, ut = "$lit$", v = `lit$${Math.random().toFixed(9).slice(2)}$`, pt = "?" + v, kt = `<${pt}>`, _ = document, U = () => _.createComment(""), O = (o) => o === null || typeof o != "object" && typeof o != "function", F = Array.isArray, Pt = (o) => F(o) || typeof (o == null ? void 0 : o[Symbol.iterator]) == "function", z = `[ 	
\f\r]`, S = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, et = /-->/g, st = />/g, $ = RegExp(`>|${z}(?:([^\\s"'>=/]+)(${z}*=${z}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), it = /'/g, ot = /"/g, mt = /^(?:script|style|textarea|title)$/i, Ct = (o) => (t, ...e) => ({ _$litType$: o, strings: t, values: e }), nt = Ct(1), E = Symbol.for("lit-noChange"), h = Symbol.for("lit-nothing"), rt = /* @__PURE__ */ new WeakMap(), g = _.createTreeWalker(_, 129);
function ft(o, t) {
  if (!F(o) || !o.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return tt !== void 0 ? tt.createHTML(t) : t;
}
const Ut = (o, t) => {
  const e = o.length - 1, s = [];
  let i, r = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", n = S;
  for (let l = 0; l < e; l++) {
    const a = o[l];
    let d, u, c = -1, m = 0;
    for (; m < a.length && (n.lastIndex = m, u = n.exec(a), u !== null); ) m = n.lastIndex, n === S ? u[1] === "!--" ? n = et : u[1] !== void 0 ? n = st : u[2] !== void 0 ? (mt.test(u[2]) && (i = RegExp("</" + u[2], "g")), n = $) : u[3] !== void 0 && (n = $) : n === $ ? u[0] === ">" ? (n = i ?? S, c = -1) : u[1] === void 0 ? c = -2 : (c = n.lastIndex - u[2].length, d = u[1], n = u[3] === void 0 ? $ : u[3] === '"' ? ot : it) : n === ot || n === it ? n = $ : n === et || n === st ? n = S : (n = $, i = void 0);
    const f = n === $ && o[l + 1].startsWith("/>") ? " " : "";
    r += n === S ? a + kt : c >= 0 ? (s.push(d), a.slice(0, c) + ut + a.slice(c) + v + f) : a + v + (c === -2 ? l : f);
  }
  return [ft(o, r + (o[e] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), s];
};
class T {
  constructor({ strings: t, _$litType$: e }, s) {
    let i;
    this.parts = [];
    let r = 0, n = 0;
    const l = t.length - 1, a = this.parts, [d, u] = Ut(t, e);
    if (this.el = T.createElement(d, s), g.currentNode = this.el.content, e === 2 || e === 3) {
      const c = this.el.content.firstChild;
      c.replaceWith(...c.childNodes);
    }
    for (; (i = g.nextNode()) !== null && a.length < l; ) {
      if (i.nodeType === 1) {
        if (i.hasAttributes()) for (const c of i.getAttributeNames()) if (c.endsWith(ut)) {
          const m = u[n++], f = i.getAttribute(c).split(v), H = /([.?@])?(.*)/.exec(m);
          a.push({ type: 1, index: r, name: H[2], strings: f, ctor: H[1] === "." ? Tt : H[1] === "?" ? Nt : H[1] === "@" ? Ht : D }), i.removeAttribute(c);
        } else c.startsWith(v) && (a.push({ type: 6, index: r }), i.removeAttribute(c));
        if (mt.test(i.tagName)) {
          const c = i.textContent.split(v), m = c.length - 1;
          if (m > 0) {
            i.textContent = L ? L.emptyScript : "";
            for (let f = 0; f < m; f++) i.append(c[f], U()), g.nextNode(), a.push({ type: 2, index: ++r });
            i.append(c[m], U());
          }
        }
      } else if (i.nodeType === 8) if (i.data === pt) a.push({ type: 2, index: r });
      else {
        let c = -1;
        for (; (c = i.data.indexOf(v, c + 1)) !== -1; ) a.push({ type: 7, index: r }), c += v.length - 1;
      }
      r++;
    }
  }
  static createElement(t, e) {
    const s = _.createElement("template");
    return s.innerHTML = t, s;
  }
}
function w(o, t, e = o, s) {
  var i, r;
  if (t === E) return t;
  let n = s !== void 0 ? (i = e.o) == null ? void 0 : i[s] : e.l;
  const l = O(t) ? void 0 : t._$litDirective$;
  return (n == null ? void 0 : n.constructor) !== l && ((r = n == null ? void 0 : n._$AO) == null || r.call(n, !1), l === void 0 ? n = void 0 : (n = new l(o), n._$AT(o, e, s)), s !== void 0 ? (e.o ?? (e.o = []))[s] = n : e.l = n), n !== void 0 && (t = w(o, n._$AS(o, t.values), n, s)), t;
}
class Ot {
  constructor(t, e) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = e;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: e }, parts: s } = this._$AD, i = ((t == null ? void 0 : t.creationScope) ?? _).importNode(e, !0);
    g.currentNode = i;
    let r = g.nextNode(), n = 0, l = 0, a = s[0];
    for (; a !== void 0; ) {
      if (n === a.index) {
        let d;
        a.type === 2 ? d = new N(r, r.nextSibling, this, t) : a.type === 1 ? d = new a.ctor(r, a.name, a.strings, this, t) : a.type === 6 && (d = new Rt(r, this, t)), this._$AV.push(d), a = s[++l];
      }
      n !== (a == null ? void 0 : a.index) && (r = g.nextNode(), n++);
    }
    return g.currentNode = _, i;
  }
  p(t) {
    let e = 0;
    for (const s of this._$AV) s !== void 0 && (s.strings !== void 0 ? (s._$AI(t, s, e), e += s.strings.length - 2) : s._$AI(t[e])), e++;
  }
}
class N {
  get _$AU() {
    var t;
    return ((t = this._$AM) == null ? void 0 : t._$AU) ?? this.v;
  }
  constructor(t, e, s, i) {
    this.type = 2, this._$AH = h, this._$AN = void 0, this._$AA = t, this._$AB = e, this._$AM = s, this.options = i, this.v = (i == null ? void 0 : i.isConnected) ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const e = this._$AM;
    return e !== void 0 && (t == null ? void 0 : t.nodeType) === 11 && (t = e.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, e = this) {
    t = w(this, t, e), O(t) ? t === h || t == null || t === "" ? (this._$AH !== h && this._$AR(), this._$AH = h) : t !== this._$AH && t !== E && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : Pt(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== h && O(this._$AH) ? this._$AA.nextSibling.data = t : this.T(_.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    var e;
    const { values: s, _$litType$: i } = t, r = typeof i == "number" ? this._$AC(t) : (i.el === void 0 && (i.el = T.createElement(ft(i.h, i.h[0]), this.options)), i);
    if (((e = this._$AH) == null ? void 0 : e._$AD) === r) this._$AH.p(s);
    else {
      const n = new Ot(r, this), l = n.u(this.options);
      n.p(s), this.T(l), this._$AH = n;
    }
  }
  _$AC(t) {
    let e = rt.get(t.strings);
    return e === void 0 && rt.set(t.strings, e = new T(t)), e;
  }
  k(t) {
    F(this._$AH) || (this._$AH = [], this._$AR());
    const e = this._$AH;
    let s, i = 0;
    for (const r of t) i === e.length ? e.push(s = new N(this.O(U()), this.O(U()), this, this.options)) : s = e[i], s._$AI(r), i++;
    i < e.length && (this._$AR(s && s._$AB.nextSibling, i), e.length = i);
  }
  _$AR(t = this._$AA.nextSibling, e) {
    var s;
    for ((s = this._$AP) == null ? void 0 : s.call(this, !1, !0, e); t && t !== this._$AB; ) {
      const i = t.nextSibling;
      t.remove(), t = i;
    }
  }
  setConnected(t) {
    var e;
    this._$AM === void 0 && (this.v = t, (e = this._$AP) == null || e.call(this, t));
  }
}
class D {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, e, s, i, r) {
    this.type = 1, this._$AH = h, this._$AN = void 0, this.element = t, this.name = e, this._$AM = i, this.options = r, s.length > 2 || s[0] !== "" || s[1] !== "" ? (this._$AH = Array(s.length - 1).fill(new String()), this.strings = s) : this._$AH = h;
  }
  _$AI(t, e = this, s, i) {
    const r = this.strings;
    let n = !1;
    if (r === void 0) t = w(this, t, e, 0), n = !O(t) || t !== this._$AH && t !== E, n && (this._$AH = t);
    else {
      const l = t;
      let a, d;
      for (t = r[0], a = 0; a < r.length - 1; a++) d = w(this, l[s + a], e, a), d === E && (d = this._$AH[a]), n || (n = !O(d) || d !== this._$AH[a]), d === h ? t = h : t !== h && (t += (d ?? "") + r[a + 1]), this._$AH[a] = d;
    }
    n && !i && this.j(t);
  }
  j(t) {
    t === h ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Tt extends D {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === h ? void 0 : t;
  }
}
class Nt extends D {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== h);
  }
}
class Ht extends D {
  constructor(t, e, s, i, r) {
    super(t, e, s, i, r), this.type = 5;
  }
  _$AI(t, e = this) {
    if ((t = w(this, t, e, 0) ?? h) === E) return;
    const s = this._$AH, i = t === h && s !== h || t.capture !== s.capture || t.once !== s.once || t.passive !== s.passive, r = t !== h && (s === h || i);
    i && this.element.removeEventListener(this.name, this, s), r && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    var e;
    typeof this._$AH == "function" ? this._$AH.call(((e = this.options) == null ? void 0 : e.host) ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Rt {
  constructor(t, e, s) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = e, this.options = s;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    w(this, t);
  }
}
const at = j.litHtmlPolyfillSupport;
at == null || at(T, N), (j.litHtmlVersions ?? (j.litHtmlVersions = [])).push("3.2.0");
const Mt = (o, t, e) => {
  const s = (e == null ? void 0 : e.renderBefore) ?? t;
  let i = s._$litPart$;
  if (i === void 0) {
    const r = (e == null ? void 0 : e.renderBefore) ?? null;
    s._$litPart$ = i = new N(t.insertBefore(U(), r), r, void 0, e ?? {});
  }
  return i._$AI(o), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class C extends y {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this.o = void 0;
  }
  createRenderRoot() {
    var t;
    const e = super.createRenderRoot();
    return (t = this.renderOptions).renderBefore ?? (t.renderBefore = e.firstChild), e;
  }
  update(t) {
    const e = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this.o = Mt(e, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var t;
    super.connectedCallback(), (t = this.o) == null || t.setConnected(!0);
  }
  disconnectedCallback() {
    var t;
    super.disconnectedCallback(), (t = this.o) == null || t.setConnected(!1);
  }
  render() {
    return E;
  }
}
var lt;
C._$litElement$ = !0, C.finalized = !0, (lt = globalThis.litElementHydrateSupport) == null || lt.call(globalThis, { LitElement: C });
const ct = globalThis.litElementPolyfillSupport;
ct == null || ct({ LitElement: C });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.0");
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const p = (o) => o ?? h;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const jt = { attribute: !0, type: String, converter: M, reflect: !1, hasChanged: q }, Lt = (o = jt, t, e) => {
  const { kind: s, metadata: i } = e;
  let r = globalThis.litPropertyMetadata.get(i);
  if (r === void 0 && globalThis.litPropertyMetadata.set(i, r = /* @__PURE__ */ new Map()), r.set(e.name, o), s === "accessor") {
    const { name: n } = e;
    return { set(l) {
      const a = t.get.call(this);
      t.set.call(this, l), this.requestUpdate(n, a, o);
    }, init(l) {
      return l !== void 0 && this.P(n, void 0, o), l;
    } };
  }
  if (s === "setter") {
    const { name: n } = e;
    return function(l) {
      const a = this[n];
      t.call(this, l), this.requestUpdate(n, a, o);
    };
  }
  throw Error("Unsupported decorator location: " + s);
};
function b(o) {
  return (t, e) => typeof e == "object" ? Lt(o, t, e) : ((s, i, r) => {
    const n = i.hasOwnProperty(r);
    return i.constructor.createProperty(r, n ? { ...s, wrapped: !0 } : s), n ? Object.getOwnPropertyDescriptor(i, r) : void 0;
  })(o, t, e);
}
var It = Object.defineProperty, k = (o, t, e, s) => {
  for (var i = void 0, r = o.length - 1, n; r >= 0; r--)
    (n = o[r]) && (i = n(t, e, i) || i);
  return i && It(t, e, i), i;
};
function Dt(o) {
  class t extends o {
    /** @ignore */
    renderAnchor({
      id: s,
      className: i,
      ariaLabel: r,
      ariaHidden: n,
      tabindex: l,
      content: a = nt`
        <slot></slot>
      `
    }) {
      return nt`
        <a
          id=${p(s)}
          class=${p(i)}
          href=${p(this.href)}
          download=${p(this.download)}
          target=${p(this.target)}
          aria-label=${p(r || void 0)}
          aria-hidden=${p(n ? "true" : void 0)}
          tabindex=${p(l)}
          referrerpolicy=${p(this.referrerpolicy)}
          rel=${p(this.rel)}
        >
          ${a}
        </a>
      `;
    }
  }
  return k([
    b()
  ], t.prototype, "download"), k([
    b()
  ], t.prototype, "href"), k([
    b()
  ], t.prototype, "target"), k([
    b()
  ], t.prototype, "referrerpolicy"), k([
    b()
  ], t.prototype, "rel"), t;
}
function zt(o, t, e = C) {
  const s = class extends e {
  };
  return s.styles = $t([o]), s.shadowRootOptions = {
    mode: "open",
    delegatesFocus: !0
  }, s.version = t, s;
}
const Bt = Symbol("internals");
function Vt(o) {
  class t extends o {
    /** @ignore */
    get [Bt]() {
      return this._internals || (this._internals = this.attachInternals()), this._internals;
    }
  }
  return t;
}
var Kt = Object.defineProperty, Wt = Object.getOwnPropertyDescriptor, B = (o, t, e, s) => {
  for (var i = s > 1 ? void 0 : s ? Wt(t, e) : t, r = o.length - 1, n; r >= 0; r--)
    (n = o[r]) && (i = (s ? n(t, e, i) : n(i)) || i);
  return s && i && Kt(t, e, i), i;
};
function qt(o) {
  class t extends o {
    constructor() {
      super(...arguments), this.disabled = !1, this.autofocus = !1;
    }
    get tabIndex() {
      const s = this.hasAttribute("tabindex") ? Number(this.getAttribute("tabindex")) : NaN;
      return this.disabled || s < 0 ? -1 : !this.focusElement || this.focusElement === this ? isNaN(s) ? -1 : s : this.focusElement.tabIndex;
    }
    set tabIndex(s) {
      if (this.focusElement === this) {
        this.setAttribute("tabindex", this.disabled ? "-1" : "" + s);
        return;
      }
      (s === -1 || this.disabled) && this.setAttribute("tabindex", "-1"), this._manageFocusElementTabIndex(s);
    }
    async _manageFocusElementTabIndex(s) {
      this.focusElement || await this.updateComplete, s === null ? this.focusElement.removeAttribute("tabindex") : this.focusElement.tabIndex = s;
    }
    focus(s) {
      this.disabled || !this.focusElement || (this.focusElement !== this ? this.focusElement.focus(s) : HTMLElement.prototype.focus.apply(this, [s]));
    }
    blur() {
      this.focusElement && (this.focusElement !== this ? this.focusElement.blur() : HTMLElement.prototype.blur.apply(this));
    }
    click() {
      this.disabled || !this.focusElement || (this.focusElement !== this ? this.focusElement.click() : HTMLElement.prototype.click.apply(this));
    }
    get focusElement() {
      return this;
    }
  }
  return B([
    b({ type: Boolean, reflect: !0 })
  ], t.prototype, "disabled", 2), B([
    b({ type: Boolean })
  ], t.prototype, "autofocus", 2), B([
    b({ type: Number })
  ], t.prototype, "tabIndex", 1), t;
}
function Ft(o, t, e) {
  const s = e ? `${t}-${e}` : t;
  customElements.get(s) ? console.warn(
    `WARN: You are importing '${s}' more than once, which is not supported. Please remove the duplicated import.`
  ) : customElements.define(s, o);
}
const Jt = ":host{-webkit-appearance:none;-moz-appearance:none;appearance:none;display:flex;align-items:center;justify-content:center;outline:none;border:none;border-radius:4px;background-color:var(--minimal-icon-button-color-background, transparent);cursor:pointer;padding:4px;max-width:max-content;height:fit-content;max-height:max-content;text-decoration:none;color:var(--minimal-icon-button-color-text)}:host([variant=dark]){--minimal-icon-button-color-text: #356799;--minimal-icon-button-color-outline-focus: #7194b7;--minimal-icon-button-color-text-disabled: #a8b4bf;--minimal-icon-button-color-background-hover: rgba(53, 103, 153, .1);--minimal-icon-button-color-background-active: rgba(53, 103, 153, .2)}:host([variant=light]){--minimal-icon-button-color-text: #fff;--minimal-icon-button-color-outline-focus: #dae3ec;--minimal-icon-button-color-text-disabled: #8393a3;--minimal-icon-button-color-background-hover: rgba(255, 255, 255, .1);--minimal-icon-button-color-background-active: rgba(255, 255, 255, .2)}:host([variant=light-filled]){--minimal-icon-button-color-text: #fff;--minimal-icon-button-color-background: rgba(255, 255, 255, .2);--minimal-icon-button-color-outline-focus: #dae3ec;--minimal-icon-button-color-text-disabled: #8393a3;--minimal-icon-button-color-background-hover: rgba(255, 255, 255, .1);--minimal-icon-button-color-background-active: rgba(255, 255, 255, .2)}:host(:focus-visible){outline:2px dashed var(--minimal-icon-button-color-outline-focus)}:host(:disabled){cursor:default;color:var(--minimal-icon-button-color-text-disabled);pointer-events:none}:host(:hover){background:var(--minimal-icon-button-color-background-hover, var(--minimal-icon-button-color-background, transparent))}:host(:active){background:var(--minimal-icon-button-color-background-active, var(--minimal-icon-button-color-background, transparent))}", Zt = "@muse/minimal-icon-button", Gt = "12.0.1", Yt = "MUSE Minimal Icon Button Component", Qt = {
  type: "git",
  url: "https://partner.bdr.de/gitlab/bun/alpha/muse-components"
}, Xt = "module", te = {
  ".": "./dist/index.js",
  "./register.js": "./dist/register.js",
  "./package.json": "./package.json"
}, ee = "./dist/index.js", se = "./dist/index.d.ts", ie = [
  "dist/"
], oe = {
  build: "vite build",
  "build:watch": "vite build --watch",
  clean: "rm -rf node_modules dist .turbo coverage",
  test: "vitest run",
  "test:coverage": "vitest run --coverage",
  "test:watch": "vitest --watch"
}, ne = {
  "@muse/utils": "workspace:^",
  lit: "^3.2.0"
}, re = {
  "@muse-dev/tsconfig": "workspace:^",
  "@muse-dev/vite-config": "workspace:^",
  "@muse-scss/atoms": "workspace:^",
  "@muse-scss/minimal-icon-button": "workspace:^",
  "@muse/icon": "workspace:^",
  vite: "^5.4.1",
  vitest: "^2.0.5"
}, ae = {
  "@muse/icon": "workspace:^"
}, le = {
  name: Zt,
  version: Gt,
  description: Yt,
  repository: Qt,
  type: Xt,
  exports: te,
  main: ee,
  types: se,
  files: ie,
  scripts: oe,
  dependencies: ne,
  devDependencies: re,
  peerDependencies: ae
};
var ce = Object.defineProperty, J = (o, t, e, s) => {
  for (var i = void 0, r = o.length - 1, n; r >= 0; r--)
    (n = o[r]) && (i = n(t, e, i) || i);
  return i && ce(t, e, i), i;
};
const he = "muse-minimal-icon-button", ht = zt(Jt, le.version), I = class I extends qt(
  Dt(Vt(ht))
) {
  constructor() {
    super(...arguments), this.variant = "dark", this.role = "button", this.type = "button";
  }
  //#endregion
  //#region -- State
  //#endregion
  //#region -- DOM References
  //#endregion
  //#region -- Computed
  //#endregion
  //#region -- Lifecycles
  connectedCallback() {
    super.connectedCallback(), this.addEventListener("click", this.handleClickCapture, {
      capture: !0
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.removeEventListener("click", this.handleClickCapture, {
      capture: !0
    });
  }
  firstUpdated(t) {
    super.firstUpdated(t), this.hasAttribute("tabindex") || this.setAttribute("tabindex", "0"), this.addEventListener("keydown", this.handleKeydown), this.addEventListener("keypress", this.handleKeypress);
  }
  //#endregion
  //#region -- Methods
  /** @ignore */
  handleClickCapture(t) {
    if (this.disabled)
      return t.preventDefault(), t.stopImmediatePropagation(), t.stopPropagation(), !1;
  }
  /** @ignore */
  click() {
    this.disabled || super.click();
  }
  /** @ignore */
  handleKeydown(t) {
    const { code: e } = t;
    switch (e) {
      case "Space":
        t.preventDefault(), this.addEventListener("keyup", this.handleKeyup);
        break;
    }
  }
  /** @ignore */
  handleKeypress(t) {
    const { code: e } = t;
    switch (e) {
      case "Enter":
      case "NumpadEnter":
        this.click();
        break;
    }
  }
  /** @ignore */
  handleKeyup(t) {
    const { code: e } = t;
    switch (e) {
      case "Space":
        this.removeEventListener("keyup", this.handleKeyup), this.click();
        break;
    }
  }
  //#endregion
  //#region -- Events
  //#endregion
  //#region -- Render
  render() {
    return vt`
      <slot></slot>
    `;
  }
  //#endregion
};
I.shadowRootOptions = {
  ...ht.shadowRootOptions,
  delegatesFocus: !1
}, I.formAssociated = !0;
let x = I;
J([
  V({ reflect: !0 })
], x.prototype, "variant");
J([
  V({ reflect: !0 })
], x.prototype, "role");
J([
  V({ reflect: !0 })
], x.prototype, "type");
const me = (o) => Ft(x, he, o);
export {
  x as M,
  me as r
};
